import {
  useAllCounterpartiesFilter,
  useCustomerCounterpartiesFilter,
} from '@src/hooks/filters/useAllCounterpartiesFilter';
import { isExcludedByResolvedUsers, useResolvedUsers } from '@src/hooks/filters/useUsersFilter';
import { useUser } from '@src/hooks/useUser';
import { useSubAccounts } from '@src/providers';
import type { useContextBlotterFilter } from '@src/providers/ContextBlotterFilterProvider/useContextBlotterFilter';
import {
  EMPTY_OBJECT,
  FilterClauseType,
  IconName,
  MultilegReportingTypeEnum,
  PricingModeEnum,
  TradeStatusEnum,
  cleanupInitialFilterDateRange,
  filterExistsAndExcludes,
  isExcludedByCurrencyOrBasePairFilters,
  removeEmptyFilters,
  useBasePairFilter,
  useCurrencyOfSymbolFilter,
  useDateRangeFilter,
  useSecuritiesContext,
  useTradingMarketAccountsFilter,
  type BlotterTableClientLocalFilter,
  type BlotterTableFilter,
  type BlotterTableFiltersProps,
  type DateRangeFilter,
  type FilterClause,
  type FilterableProperty,
  type Trade,
  type UseFilterBuilderProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { isEqual, keys, startCase, values } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import {
  useFeatureFlag,
  useIDFilter,
  useMarketsFilter,
  useSidesFilter,
  useSubAccountsFilter,
  useUsersFilter,
} from '../../../hooks';
import {
  predictionMarketColIDToFilterKey,
  rowMatchesPredictionMarketBlotterFilters,
  usePredictionMarketBlotterFilterProperties,
  usePredictionMarketBlotterLookup,
  type PredictionMarketBlotterFilter,
} from '../predictionMarketBlotter';

export const MultilegView = { Parent: 'Parent', Child: 'Child', Both: 'Both' } as const;
export type MultilegView = (typeof MultilegView)[keyof typeof MultilegView];

export interface BlotterTableTradeFilter extends DateRangeFilter, PredictionMarketBlotterFilter {
  Markets?: string[];
  MarketAccounts?: string[];
  Sides?: string[];
  Statuses?: string[];
  SubAccounts?: string[];
  Symbols?: string[];
  Users?: string[];
  HideApiCalls?: boolean;
  HideOffPlatform?: boolean;
  OrderID?: string;
  RFQID?: string;
  PricingMode?: string[];
  MultilegView?: MultilegView;
  Customers?: string[];
  Counterparties?: string[];
  Currencies?: string[];
  BasePairs?: string[];
}

export type TradeAdditionalFilterState = ReturnType<typeof useContextBlotterFilter>['additionalFilterState'];

interface UseTradeFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
  /**
   * When true, hides the OrderID and RFQID filter builder options, since the
   * blotter is already scoped to a specific order and those filters would be redundant.
   */
  filterByOrderID: boolean;
  additionalFilterState: TradeAdditionalFilterState;
  /** Fallback MultilegView when the persisted filter has no MultilegView set. */
  defaultMultilegView?: MultilegView;
  enableTradesUserBackendFiltering?: boolean;
}

export type UseTradeFilterOutput<TData extends Trade> = {
  initialFilter: BlotterTableTradeFilter | undefined;
  filter: BlotterTableTradeFilter;
  clientSideFilter: BlotterTableClientLocalFilter<TData>;
  changeFilter: (action: SetStateAction<BlotterTableFilter>) => void;
  filterableProperties: FilterableProperty[];
  filterBuilderProps: UseFilterBuilderProps;
  blotterTableFilterProps: Partial<BlotterTableFiltersProps>;
};

export function useTradeFilter<TData extends Trade>({
  persistedBlotterTable,
  filterByOrderID,
  additionalFilterState,
  defaultMultilegView,
  enableTradesUserBackendFiltering = false,
}: UseTradeFilterParams): UseTradeFilterOutput<TData> {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const { Name: currentUserName } = useUser();

  const [initialFilter] = useState(() => persistedBlotterTable.initialState?.filter);
  const [filter, setFilter] = useState<BlotterTableTradeFilter>(() => {
    const base = cleanupInitialFilterDateRange(initialFilter);
    return { ...base, MultilegView: base.MultilegView ?? defaultMultilegView };
  });

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const prevFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;
      if (!isEqual(prevFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const resolvedUsers = useResolvedUsers(filter.Users, currentUserName);
  const { subAccountsEnabled } = useSubAccounts();
  const { securitiesList, securitiesBySymbol } = useSecuritiesContext();
  const { enablePredictionMarkets } = useFeatureFlag();
  const predictionMarketFilterProperties = usePredictionMarketBlotterFilterProperties();
  const predictionMarketLookup = usePredictionMarketBlotterLookup();

  const clientSideFilter = useCallback<BlotterTableClientLocalFilter<TData>>(
    row => {
      const data = row.data;
      if (!data) {
        return false;
      }

      const activeFilter = {
        ...filter,
        ...additionalFilterState,
      };

      if (filterExistsAndExcludes(activeFilter, 'MarketAccounts', data, 'MarketAccount')) {
        return false;
      }
      if (filterExistsAndExcludes(activeFilter, 'Sides', data, 'Side')) {
        return false;
      }
      if (filterExistsAndExcludes(activeFilter, 'Statuses', data, 'TradeStatus')) {
        return false;
      }
      if (isExcludedByResolvedUsers(resolvedUsers, data?.User)) {
        return false;
      }
      if (filterExistsAndExcludes(filter, 'PricingMode', data, 'PricingMode')) {
        return false;
      }
      if (filterExistsAndExcludes(activeFilter, 'Symbols', data, 'Symbol')) {
        return false;
      }
      if (
        isExcludedByCurrencyOrBasePairFilters(
          data.Symbol,
          securitiesBySymbol,
          activeFilter.Currencies,
          activeFilter.BasePairs
        )
      ) {
        return false;
      }
      if (isTradeExcludedByCounterpartyFilters(data, activeFilter)) {
        return false;
      }
      if (
        activeFilter.SubAccounts &&
        activeFilter.SubAccounts.length > 0 &&
        !(
          (data.SubAccount && activeFilter.SubAccounts.includes(data.SubAccount)) ||
          data.Allocation?.Allocations.some(alloc => filter.SubAccounts?.includes(alloc.SubAccount))
        )
      ) {
        return false;
      }

      const isMultilegParent = data?.MultilegReportingType === MultilegReportingTypeEnum.Parent;
      const isMultilegChild = data?.MultilegReportingType === MultilegReportingTypeEnum.Leg;
      const multilegView = activeFilter.MultilegView ?? MultilegView.Both;
      if (multilegView === MultilegView.Child && isMultilegParent) {
        return false;
      }
      if (multilegView === MultilegView.Parent && isMultilegChild) {
        return false;
      }
      if (!enableTradesUserBackendFiltering && isTradeExcludedByOffPlatformFilter(data, activeFilter)) {
        return false;
      }
      if (
        enablePredictionMarkets &&
        !rowMatchesPredictionMarketBlotterFilters(data.Symbol, activeFilter, predictionMarketLookup)
      ) {
        return false;
      }
      return true;
    },
    [
      additionalFilterState,
      enablePredictionMarkets,
      enableTradesUserBackendFiltering,
      filter,
      predictionMarketLookup,
      resolvedUsers,
      securitiesBySymbol,
    ]
  );

  const customersFilter = useCustomerCounterpartiesFilter();
  const counterpartiesFilter = useAllCounterpartiesFilter({ label: 'Counterparty' });

  const tradingMarketAccountsFilter = useTradingMarketAccountsFilter();
  const marketsFilter = useMarketsFilter({ onlyTradingMarkets: true });
  const subAccountsFilter = useSubAccountsFilter(EMPTY_OBJECT);
  const usersFilter = useUsersFilter();
  const sidesFilter = useSidesFilter();
  const idFilter = useIDFilter();
  const currencyOfSymbolFilter = useCurrencyOfSymbolFilter();
  const basePairFilter = useBasePairFilter();

  const filterableProperties: FilterableProperty[] = useMemo(
    () =>
      [
        marketsFilter,
        tradingMarketAccountsFilter,
        customersFilter,
        counterpartiesFilter,
        sidesFilter,
        {
          key: 'Statuses',
          label: 'Status',
          icon: IconName.CheckCircle,
          options: values(TradeStatusEnum),
          getOptionLabel: (option: string) => option,
        },
        subAccountsEnabled ? subAccountsFilter : undefined,
        {
          key: 'Symbols',
          label: 'Symbol',
          icon: IconName.CurrencyDollar,
          options: securitiesList.map(sec => sec.Symbol),
          getOptionLabel: (option: string) => securitiesBySymbol.get(option)?.DisplaySymbol || option,
        },
        currencyOfSymbolFilter,
        basePairFilter,
        {
          key: 'PricingMode',
          label: 'Pricing Mode',
          icon: IconName.CurrencyDollar,
          options: values(PricingModeEnum),
          getOptionLabel: startCase,
        },
        usersFilter,
        !filterByOrderID ? { ...idFilter, key: 'OrderID', label: 'Order ID' } : null,
        !filterByOrderID ? { ...idFilter, key: 'RFQID', label: 'RFQ ID' } : null,
        ...predictionMarketFilterProperties,
      ].compact(),
    [
      counterpartiesFilter,
      customersFilter,
      filterByOrderID,
      idFilter,
      marketsFilter,
      securitiesBySymbol,
      securitiesList,
      currencyOfSymbolFilter,
      basePairFilter,
      sidesFilter,
      subAccountsEnabled,
      subAccountsFilter,
      tradingMarketAccountsFilter,
      usersFilter,
      predictionMarketFilterProperties,
    ]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof BlotterTableTradeFilter)[]).forEach((key: keyof BlotterTableTradeFilter) => {
        if (!enablePredictionMarkets && (key === 'SeriesIDs' || key === 'EventIDs')) {
          return;
        }
        switch (key) {
          case '_start':
          case 'StartDate':
          case 'EndDate':
          case 'TimestampField':
          case 'MultilegView':
          case 'HideApiCalls':
          case 'HideOffPlatform':
            return;
          default:
            clauses.push({
              key: key,
              type: FilterClauseType.INCLUSIVE,
              selections: filter[key] as string[],
            });
        }
      });
    }
    return clauses;
  }, [enablePredictionMarkets, filter]);
  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter: BlotterTableTradeFilter = removeEmptyFilters<BlotterTableTradeFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => {
              let selections: undefined | string | string[];
              switch (key) {
                case 'OrderID':
                case 'RFQID':
                  // below are exact search and only one value is supported by backend
                  selections = filterClausesByPropertyKey.get(key)?.selections?.[0];
                  break;
                default:
                  selections = filterClausesByPropertyKey.get(key)?.selections;
              }
              return [key, selections];
            })
          ) satisfies BlotterTableTradeFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );
  const dateRangeFilter = useDateRangeFilter(filter, changeFilter, additionalFilterState?.DateRange);

  const filterBuilderProps = useMemo(
    () => ({
      initialFilterClauses,
      properties: filterableProperties,
      onFilterClausesChanged: handleFilterClausesChanged,
    }),
    [filterableProperties, handleFilterClausesChanged, initialFilterClauses]
  ) satisfies UseFilterBuilderProps;

  const blotterTableFilterProps = useMemo(
    () => ({
      ...dateRangeFilter,
    }),
    [dateRangeFilter]
  ) satisfies Partial<BlotterTableFiltersProps>;

  return {
    initialFilter,
    filter,
    clientSideFilter,
    changeFilter,
    filterableProperties,
    // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
    filterBuilderProps,
    // shortcut to spread props into the BlotterTableFilters component
    blotterTableFilterProps,
  };
}

// Map from a ag-grid columnID to a filterbuilder key
// Returns undefined if the column can not be mapped to a filter builder clause
export function colIDToFilterBuilderKey(id: string): string | undefined {
  switch (id) {
    case 'Symbol':
      return 'Symbols';
    case 'SubAccount':
      return 'SubAccounts';
    case 'Market':
      return 'Markets';
    case 'MarketAccount':
      return 'MarketAccounts';
    case 'Side':
      return 'Sides';
    case 'TradeStatus':
      return 'Statuses';
    case 'User':
      return 'Users';
    case 'OrderID':
      return 'OrderID';
    case 'RFQID':
      return 'RFQID';
    case 'Counterparty':
      return 'Customers';
    case 'CounterpartyDisplay':
      return 'Counterparties';
  }
  return predictionMarketColIDToFilterKey(id);
}

export function isTradeExcludedByCounterpartyFilters(
  data: Pick<Trade, 'Counterparty'>,
  filter: Pick<BlotterTableTradeFilter, 'Customers' | 'Counterparties'>
): boolean {
  return (
    filterExistsAndExcludes(filter, 'Customers', data, 'Counterparty') ||
    filterExistsAndExcludes(filter, 'Counterparties', data, 'Counterparty')
  );
}

export function isOffPlatformTrade(data: { User?: string | null }): boolean {
  return data.User == null || data.User === '';
}

export function isTradeExcludedByOffPlatformFilter(
  data: { User?: string | null },
  filter: Pick<BlotterTableTradeFilter, 'HideOffPlatform'>
): boolean {
  return !!filter.HideOffPlatform && isOffPlatformTrade(data);
}
